import React, { useState } from 'react';
import { V4 } from '../tokens';

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'dark'
  | 'outline'
  | 'success'
  | 'danger'
  | 'outlineDanger'
  | 'whatsapp'
  | 'ghostDanger'
  | 'ghost';

export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps {
  children?: React.ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ReactNode;
  iconLeft?: React.ReactNode;
  iconOnly?: boolean;
  fullWidth?: boolean;
  disabled?: boolean;
  loading?: boolean;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  title?: string;
  'aria-label'?: string;
  // Botón que abre/cierra contenido (patrón disclosure). Sin esto el lector de
  // pantalla no anuncia si lo que el botón controla está abierto o cerrado.
  'aria-expanded'?: boolean;
  'data-testid'?: string;
}

const STYLES: Record<ButtonVariant, { bg: string; fg: string; bd: string; hover: string }> = {
  primary: { bg: V4.secondary, fg: V4.white, bd: V4.secondary, hover: V4.secondaryDark },
  secondary: { bg: V4.white, fg: V4.primary, bd: V4.primary, hover: V4.grayUltraLight },
  dark: { bg: V4.primaryDark, fg: V4.white, bd: V4.primaryDark, hover: V4.primaryDeepest },
  outline: { bg: V4.white, fg: V4.primary, bd: V4.gray, hover: V4.grayUltraLight },
  success: { bg: V4.accentMint, fg: V4.white, bd: V4.accentMint, hover: '#18a582' },
  danger: { bg: V4.danger, fg: V4.white, bd: V4.danger, hover: '#E01818' },
  // Destructivo con borde y fondo claro: se lee como BOTÓN (a diferencia del ghostDanger
  // transparente, que parecía un link), sin ser el más llamativo de una barra de operación.
  // texto dangerDark (#E01818, 4.88:1 sobre blanco) + borde danger (#FF3939).
  outlineDanger: { bg: V4.white, fg: V4.dangerDark, bd: V4.danger, hover: V4.dangerFaded },
  whatsapp: { bg: '#25D366', fg: V4.white, bd: '#25D366', hover: V4.success },
  ghostDanger: { bg: 'transparent', fg: V4.dangerDark, bd: 'transparent', hover: 'transparent' },
  ghost: { bg: 'transparent', fg: V4.primary, bd: 'transparent', hover: V4.grayUltraLight },
};

function Spinner({ size = 16, color = 'currentColor' }: { size?: number; color?: string }) {
  return (
    <span style={{ display: 'inline-flex', width: size, height: size, color }} aria-hidden="true">
      <svg
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        style={{ animation: 'plick-spin 720ms linear infinite' }}
      >
        <circle cx="12" cy="12" r="9" stroke="currentColor" strokeOpacity=".22" strokeWidth="2.5" />
        <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    </span>
  );
}

export function Button({
  children,
  variant = 'primary',
  size = 'md',
  icon,
  iconLeft,
  iconOnly = false,
  fullWidth = false,
  disabled = false,
  loading = false,
  onClick,
  type = 'button',
  title,
  'aria-label': ariaLabel,
  'aria-expanded': ariaExpanded,
  'data-testid': dataTestId,
}: ButtonProps) {
  const [hover, setHover] = useState(false);
  const [pressed, setPressed] = useState(false);

  const h = size === 'sm' ? 32 : size === 'lg' ? 44 : 38;
  const padX = size === 'sm' ? 14 : size === 'lg' ? 20 : 16;
  const padY = size === 'sm' ? 6 : 8;
  const fs = size === 'sm' ? 11 : 12;
  const s = STYLES[variant];
  // `disabled || loading` — el botón debe quedar inhabilitado (HTML disabled, sin foco
  // teclado, opacity reducida, pointer-events: none) tanto si el padre lo marca como
  // disabled como si está en estado de carga. El cursor sigue siendo distinto:
  // 'progress' para loading, 'default' para disabled puro.
  const isDisabled = disabled || loading;

  const cursor = loading ? 'progress' : isDisabled ? 'default' : 'pointer';

  return (
    <button
      type={type}
      onClick={loading ? undefined : onClick}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      title={title}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      data-testid={dataTestId}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => {
        setHover(false);
        setPressed(false);
      }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onBlur={() => {
        setHover(false);
        setPressed(false);
      }}
      style={{
        // minHeight (no height fijo): un label largo en un boton fullWidth angosto (mobile) debe
        // poder crecer a 2 lineas en vez de desbordarse cortado — height fijo + nowrap se lo
        // impedia (hallazgo real: "Elegir Plan Profesional Mensual" se cortaba en 320-390px).
        minHeight: h,
        width: iconOnly ? h : fullWidth ? '100%' : 'auto',
        padding: iconOnly ? 0 : `${padY}px ${padX}px`,
        borderRadius: 6,
        border: `1.5px solid ${s.bd}`,
        background: hover && !isDisabled && !loading ? s.hover : s.bg,
        color: s.fg,
        fontWeight: 700,
        fontSize: fs,
        lineHeight: 1.3,
        cursor,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
        display: 'inline-flex',
        alignItems: 'center',
        gap: iconOnly ? 0 : 6,
        justifyContent: 'center',
        textAlign: 'center',
        opacity: isDisabled ? 0.35 : 1,
        pointerEvents: isDisabled ? 'none' : 'auto',
        fontFamily: 'inherit',
        transition: 'background 150ms, color 150ms, transform 90ms cubic-bezier(.4,0,.2,1), box-shadow 90ms',
        // 'normal' (envuelve en vez de desbordarse): iconOnly nunca tiene texto que envolver, asi
        // que no hace falta bifurcar aqui.
        whiteSpace: 'normal',
        transform: pressed && !isDisabled ? 'translateY(1px) scale(0.985)' : 'translateY(0) scale(1)',
        boxShadow: pressed && !isDisabled ? 'inset 0 2px 4px rgba(0,0,0,.08)' : 'none',
      }}
    >
      {loading ? (
        <Spinner size={16} />
      ) : (
        <>
          {iconLeft && !iconOnly && (
            <span style={{ fontSize: 15, display: 'inline-flex', alignItems: 'center' }}>{iconLeft}</span>
          )}
          {!iconOnly && children}
          {icon && <span style={{ fontSize: 15, display: 'inline-flex', alignItems: 'center' }}>{icon}</span>}
        </>
      )}
    </button>
  );
}
